/*
*  Search Menu
*  Applied: views/industries
*  Description: Allows user to toggle between the database sets eg. documents, collections, and bibliography
*/

import './styles.scss';
import React, {Dispatch, useCallback, useEffect, useState} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../constants";
import Utils from "../../utils";
import {ISearchStateMenuItem} from "./interface";
import {Category} from "../../api/payload/payload-types";

/* Bootstrap */
import {Nav, Navbar} from "react-bootstrap";

/* Redux */
import {useDispatch, useSelector} from "react-redux";
import {ISearchState} from "../../types/redux/search";
import {RootState} from "../../redux";
import {IContentPage, IContentState} from "../../types/redux/content";
import {AnyAction} from "@reduxjs/toolkit";
import {setContentCategory} from "../../redux/slices/content-slice";

/* Hooks */
import useSearchReset from "../../hooks/useSearchReset";
import {addCrumb} from "../../redux/slices/breadcrumb-slice";
import ViewSubheader from "../view/view-subheader";
import {useLocation, useNavigate} from "react-router-dom";

const IndustryNavbar = ():JSX.Element => {
  const CONTENT_NAV_ORDER: string[] = [Constants.ABOUT, Constants.DOCUMENTS, Constants.PUBLICATIONS, Constants.COLLECTIONS, Constants.MORE_RESOURCES];
  const items: ISearchStateMenuItem[] = Constants.HOME_SEARCH_MENU_ITEMS;
  const dispatch:Dispatch<AnyAction> = useDispatch();

  /* redux */
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);

  const contentState: IContentState = useSelector<RootState, IContentState>((state: RootState):any => state.content);
  const contentItemsSrc: IContentPage[] | undefined = contentState.content_pages.filter(p =>  p.industries.title?.replace(' ','-').toLowerCase() === searchState.industry.toLowerCase());
  const contentItems: IContentPage[] = JSON.parse(JSON.stringify(contentItemsSrc));

  /* state */
  const [active, setActive] = useState<string>(Constants.ABOUT);
  const [expanded, setExpanded] = useState<boolean>(false);

  /* hooks */
  const searchReset: Function = useSearchReset();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(()=> {
    const navigateCategory = ()=>{
      if (contentState.categories.length > 0) {
        const { section } = Utils.parseIndustrySection();
        const categoryData: Category | undefined = contentState.categories.find(c=>section === c.title?.replace(' ', '-').toLowerCase());

        if (section && categoryData) {
          dispatch(setContentCategory(categoryData));
          setActive(categoryData.id);
        } else {
          setActive(section  ? section : Constants.DOCUMENTS);
          dispatch(setContentCategory(null));
        }

        /* redirect to about | documents if not specified for industry */
        const urlSegments: string[] = window.location.pathname.split('/');
        if (!urlSegments[2] && !Constants.GLOBAL_VIEWS.includes(urlSegments[1])) {
          navigate('/' + searchState.industry + '/' + section + '/')
        }

        const sectionRemap: string = (section === Constants.BIBLIOGRAPHY) ? Constants.PUBLICATIONS : section;
        const divider: string = (searchState.industry === Constants.ALL_INDUSTRIES) ? '' : "Industry";
        dispatch(addCrumb({
          label: Utils.getIndustryTitle(searchState.industry).industry_title + ' ' + divider +' - ' + Utils.toTitleCase(sectionRemap),
          href: '/' + searchState.industry + '/' + section.toLowerCase() + '/',
          level: 1,
          active: true
        }));
      }
    }

    navigateCategory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentState.category, contentState.categories, searchState.db_set])

  const handleDBItemClick = (e:React.MouseEvent, dbSet: string | null):void => {
    e.preventDefault();
    e.stopPropagation();

    if (dbSet === searchState.db_set) return;
    searchReset(dbSet);
    setExpanded(false);

    const url:string = '/' + searchState.industry + '/' + dbSet + '/';
    const locationSplit: string[] = location.pathname.split('/');
    if ((locationSplit[locationSplit.length-2]) === Constants.VIEWER) {
      navigate(url)
    } else {
      navigate(url)
    }
  }

  const handleContentItemClick = (e:React.MouseEvent, data: any | null):void => {
    e.stopPropagation();

    if (contentState.category?.id === data.categories.id) return;
    setExpanded(false);
    searchReset(null);

    const url:string = '/' + searchState.industry+ '/' + data.categories.title.replace(' ', '-').toLowerCase() + '/';
    const locationSplit: string[] = location.pathname.split('/');
    if ((locationSplit[locationSplit.length-2]) === Constants.VIEWER) {
      navigate(url)
    } else if (data && data.categories) {
      window.history.pushState(null, '',url);
    }

    dispatch(setContentCategory(data.categories));
  }

  const heading = ():string =>{
    let returnStr: string  = Utils.getIndustryTitle(searchState.industry).industry_title;
    if (searchState.industry !== Constants.ALL_INDUSTRIES) {
      returnStr += ' ' + Utils.toTitleCase(Constants.INDUSTRY)
    }
    return returnStr;
  }

  const navigationItems = useCallback((): JSX.Element[] => {
    const NavItem = (label: string, eventKey: string, key:string, data: any):JSX.Element => {
      return (
        <Nav.Item
          role={'presentation'}
          key={key}
          className={"rounded-1"}>

          <Nav.Link
            className={"mx-4 pt-2 pb-2 px-0 text-black"}
            onClick={e=> data.categories ? handleContentItemClick(e, data) : handleDBItemClick(e, data.db_set)}
            eventKey={eventKey}
          >
            {Utils.toTitleCase(label)}
          </Nav.Link>
          <div className={(active === eventKey) ? 'active_select' : '' }></div>
        </Nav.Item>
      )
    }

    const parseMenu = (): JSX.Element[] => {
      let pos: number = 0;
      let _navItems: (JSX.Element | number)[] = [];
      items.forEach((item:ISearchStateMenuItem, index:number)=> {
        const eventKey: string = item.db_set ? item.db_set : Constants.HIGHLIGHTS;
        pos = CONTENT_NAV_ORDER.indexOf(item.label.toLowerCase().replace(' ', '-'));
        _navItems[pos] =(NavItem(item.label, eventKey, item.label + index, item))
      })

      contentItems.forEach((item:IContentPage, index:number) => {
        const label: string = item.categories.title ? item.categories.title : '';
        pos = CONTENT_NAV_ORDER.indexOf(label.toLowerCase().replace(' ', '-'));
        _navItems[pos] = NavItem(label, item.categories.id , label + index, item)
      })

      _navItems = _navItems.filter((ni:JSX.Element | number)=>ni !== 0)
      return _navItems as JSX.Element[]
    }

    return parseMenu();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentItems, items]);

  return (
    <div className={'industry-navbar'}>
      <Navbar expand={'lg'} expanded={expanded} onToggle={()=>setExpanded(!expanded)} >
        <div className={'subheader-container w-100'}>
          <ViewSubheader
            heading={heading()}
            subHeading={Utils.getIndustryTitle(searchState.industry).archive_title}
          />

          <Navbar.Toggle
            aria-controls={"industry-navbar"}
            className={'nav-toggle d-inline-block'}
          >
            <div className={'arrow-container'}>
              <div className={(expanded? 'arrow-up': 'arrow-down')}/>
            </div>
          </Navbar.Toggle>
        </div>

        <div className={'nav-container shadow-sm w-100'}>
          <Navbar.Collapse id={'industry-navbar'}>
            <Nav
              className={'justify-content-left bg-white pt-1 mb-0 position-relative rounded-md-bottom'}
              activeKey={active}
              onSelect={(selectedKey) => setActive(selectedKey ? selectedKey : Constants.HIGHLIGHTS)}
            >
              {navigationItems()}
            </Nav>
          </Navbar.Collapse>
        </div>
      </Navbar>
    </div>
  )
}

export default IndustryNavbar;
